import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useGetCompaniesListQuery } from 'store/api/companies';
import { useGetInvoiceSpecTemplatesQuery, useGetInvoiceTemplatesQuery } from 'store/api/invoices.ts';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { EntityType } from 'types/entity';
import { OfficeForm } from 'types/offices';
import { t } from 'utils/getTranslations';
import { useFixedDeadlineEventSender } from '../useFixedDeadlineEventSender';

export const OFFICE_FORM_CLASSIFIERS = ['COUNTRY'];

const defaultValues: OfficeForm = {
    name: '',
    company: null,
    companies: [],
    emailAddress: '',
    invoicingEmailAddress: '',
    deadlineEventEmailAddress: '',
    phoneNumber: '',
    abbreviation: '',
    address: null,
    localAddress: null,
    invoiceAbbreviation: '',
    isActive: true,
    invoiceTemplates: [],
    invoiceSpecTemplates: [],
    defaultLocalCountryInvoiceTemplate: null,
    defaultForeignCountryInvoiceTemplate: null,
    defaultLocalCountryInvoiceSpecTemplate: null,
    defaultForeignCountryInvoiceSpecTemplate: null,
};

const EMPTY_CUSTOM_FIELDS: CustomField[] = [];

interface Props {
    initialData?: OfficeForm;
    classifiers: Record<string, ClassifierData>;
    entityCustomFields?: CustomField[];
}

/** The office form's state and the lists its fields offer, shared by the create and edit pages and the cards. */
export const useOfficeForm = ({ initialData, classifiers, entityCustomFields = EMPTY_CUSTOM_FIELDS }: Props) => {
    const methods = useForm<OfficeForm>({
        defaultValues: initialData ? { ...defaultValues, ...initialData } : defaultValues,
        values: initialData,
        mode: 'onBlur',
    });

    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.OFFICE,
        fields: entityCustomFields,
    });

    // Validate if email field matches email pattern
    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);

    const { data: companyList, isLoading: isLoadingCompanies } = useGetCompaniesListQuery();
    const activeCompanyList = useMemo(() => companyList?.filter((c) => c.isActive !== false) ?? [], [companyList]);
    const { data: invoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();

    // Watch for changes in invoiceTemplates to show/hide default template dropdowns
    const watchedInvoiceTemplates = methods.watch('invoiceTemplates');
    const watchedInvoiceSpecTemplates = methods.watch('invoiceSpecTemplates');
    const deadlineEventSender = useFixedDeadlineEventSender();

    return {
        methods,
        classifiers,
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCompanies || isLoadingCustomFieldData,
        validEmail,
        activeCompanyList,
        invoiceTemplates,
        invoiceSpecTemplates,
        watchedInvoiceTemplates,
        watchedInvoiceSpecTemplates,
        deadlineEventSender,
    };
};

export type OfficeFormState = ReturnType<typeof useOfficeForm>;
